import { useState, useEffect, useMemo } from "react";
import { useForm, Controller,useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ChevronDown, Pencil, Plus, Trash2 } from "lucide-react";
import { useUser } from "@/app/context/UserContext";
import FieldEditor from "./FieldEditor";
import BackendEditor from "./BackendEditor";
import { iProComponent } from "@/app/models/interfaceProject";

const fieldTypes = ["text", "email", "number", "password", "date-range", "select","api-search" ,"radio", "checkbox", "file"] as const;
const methods = ["GET", "POST", "PUT", "DELETE"] as const;
const encTypes = ["application/x-www-form-urlencoded", "multipart/form-data","application/json", "text/plain"] as const;

const apiHeaderSchema = z.object({
  key: z.string().min(1, "Clave obligatoria"),
  value: z.string().min(1, "Valor obligatorio"),
});

const apiConfigSchema = z.object({
  url: z.string().url("Debe ser una URL válida"),
  method: z.enum(["GET", "POST", "PUT", "DELETE"] as const),
  headers: z.array(apiHeaderSchema).optional(),
});

// Esquema de validación para cada campo
const fieldSchema = z.object({
    field_id: z.string().default(() => crypto.randomUUID()),
    nameStart: z.string().min(1, "El nombre es obligatorio"),
    nameEnd: z.string().optional(),
    label: z.string().min(1, "La etiqueta es obligatoria"),
    type: z.enum(fieldTypes),
    defaultValue:z.string().optional(),
    hidden:z.boolean().optional(),
    options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
    validations: z.array(
      z.object({
        type: z.enum([ "required", "minLength","maxLength","min","max","pattern","compareField",]),
        value: z.union([ z.boolean(), z.number(), z.string() ]), 
        operator: z.enum([">", ">=", "<", "<=", "==", "!="]).optional()
      })
    ).optional(),
    apiConfig: apiConfigSchema.optional()

    /*validation: z.object({
        required: z.boolean().optional(),
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        pattern: z.string().optional(),
    }).optional(),
    */
});
const headerSchema = z.object({
  key: z.string().min(1, "Clave requerida"),
  value: z.string().min(1, "Valor requerido"),
});

// Esquema de validación para el formulario completo
const formSchema = z.object({ 
  title: z.string().min(1, "El título es obligatorio"),
  action: z.string().url("Debe ser una URL válida"),
  is_backend: z.boolean().optional().default(false),
  backend_config: z.object({
    url_token: z.string().url("Debe ser una URL válida").optional(),
    fields: z.array(fieldSchema).optional(),
  }).optional(),
  method: z.enum(methods), // Aquí usamos el enum correcto
  enctype: z.enum(encTypes), // Aquí usamos el enum correcto
  redirect: z.boolean(),
  headers: z.array(headerSchema),
  fields: z.array(fieldSchema),
  labelButton: z.string().min(1, "Ingrese texto para el botón"),
});


export default function GeneratorForm({ vCompoment}: { vCompoment: iProComponent }) {
  const { formSchemaJson, setFormSchemaJson } = useUser();  // Obtén el array principal del contexto
  //const [schemaJson, setSchemaJson] = useState(vCompoment.schema); // Estado local
  const [schemaJson, setSchemaJson] = useState<FormSchemaFrm>(vCompoment?.schema);
  const cleanSchemaJson = {
  ...schemaJson,
};
  
  const { register, handleSubmit, reset , control, setValue, watch, formState: { errors, isValid } } = useForm({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    

  defaultValues: {
    ...cleanSchemaJson,
  }
  });
  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
    update: updateHeader,
  } = useFieldArray({
    control,
    name: "headers",
  });
 
  
  const watchedFields = watch();
  const computedSchema = useMemo(() => (isValid ? watchedFields : schemaJson), [watchedFields, isValid]);
  
 //useEffect(() => {
 //  const schemaString = JSON.stringify(computedSchema);
 //  const currentString = JSON.stringify(schemaJson); 
 //  if (schemaString !== currentString) {
 //    setSchemaJson(computedSchema);
 //  }
 //}, [computedSchema, schemaJson]);
  
 

 
  useEffect(() => {
    if (JSON.stringify(schemaJson) !== JSON.stringify(vCompoment.schema)) {
      setSchemaJson(vCompoment.schema);
    }
  }, [vCompoment.schema]);

  useEffect(() => {
    if (JSON.stringify(schemaJson) !== JSON.stringify(computedSchema)) {
      setFormSchemaJson((prevState: iProComponent[]) =>
        prevState.map(comp =>
          comp.form_id === vCompoment.form_id
            ? { ...comp, schema: computedSchema }
            : comp
        )
      );
    }
  }, [computedSchema]);

  const [isOpenFieldEditor, setIsOpenFieldEditor] = useState(false);
  const [fieldEdit, setFieldEdit] = useState({} as FieldFrm); 
  const [typeFrmFieldEdit, setTypeFrmFieldEdit] = useState("Edit");
  const [isOpenBackendEditor, setIsOpenBackendEditor] = useState(false);

  const handleSaveBackendConfig = (backendConfig:any) => {
    setValue("backend_config", backendConfig);
  };
  const addFieldEditor = () => {
    setTypeFrmFieldEdit("Add");
    setFieldEdit({field_id: crypto.randomUUID(),  defaultValue:"", hidden:false, nameStart: "", nameEnd: "", label: "", type: "text", validations: [], apiConfig:{"url": "", "method": "GET", "campo": "", "campo_response": "", "headers": []} } );
    setIsOpenFieldEditor(true); 
  };
  
  const editFieldEditor = (vId: string)=>{ 
    setTypeFrmFieldEdit("Edit");
    setFieldEdit( watchedFields.fields.find(O=>O.field_id ===vId) as FieldFrm);
    setIsOpenFieldEditor(true); 
  }

  const removeFieldEditor = (vId: string) => { 
    setValue( "fields", watchedFields.fields.filter(O=>O.field_id != vId) );
  };

  const handleSaveField = (updatedField: FieldFrm) => {
    console.log("Campo guardado:", updatedField);
  const currentFields = watch("fields") || []; // Obtenemos los campos actuales de forma segura
  
  if (typeFrmFieldEdit === "Add") {
    setValue("fields", [...currentFields, updatedField], { 
      shouldValidate: true, 
      shouldDirty: true 
    });
  } else {
    const newFields = currentFields.map(field =>
      field.field_id === updatedField.field_id ? updatedField : field
    );
    setValue("fields", newFields, { 
      shouldValidate: true, 
      shouldDirty: true 
    });
  }
};
 

  return (
    <div className="px-4"> 
      <form className="space-y-1">
        <label className="block text-sm font-medium">Título</label>
        <input {...register("title")} placeholder="Título" 
        className={`w-full border n-fmr-input px-3 py-2 ${errors.title? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-1 focus:ring-blue-700`} />
        {errors.title && <p className="text-red-500">{errors.title.message}</p>}

        <label className="block text-sm font-medium">Metodo</label>
        <div className="relative w-full"> 
          <select {...register("method")} 
              className= {` w-full appearance-none border n-fmr-input px-4 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700`}>
            {methods.map((m) => (<option key={m} value={m}>{m}</option>))}
          </select> 
          <ChevronDown className="w-5 h-5 absolute inset-y-0 top-3 right-3 pointer-events-none text-gray-400" /> 
        </div>

        <label className="block text-sm font-medium">URL de acción</label>
        <input {...register("action")} placeholder="URL de acción" 
                className={`w-full border n-fmr-input px-3 py-2 ${errors.action? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-1 focus:ring-blue-700`} />
                {errors.action && <p className="text-red-500">{errors.action.message}</p>}
        <div className="flex flex-col mt-3 space-y-2">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            {...register("is_backend")}
            onChange={(e) => {
              const checked = e.target.checked;
              setValue("is_backend", checked);
              if (checked) {
                setIsOpenBackendEditor(true);
              } else {
                setValue("backend_config", {});
              }
            }}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-700"
          />
          <span className="text-sm text-gray-700">Backend to Backend?</span>
        </div>

        {watch("is_backend") && (
          <button
            type="button"
            onClick={() => setIsOpenBackendEditor(true)}
            className="flex items-center justify-center gap-1 rounded-full px-3 py-1 shadow-md border border-gray-300 bg-gray-100 hover:bg-gray-200"
          >
            Editar backend
            <Pencil className="w-5 h-5" strokeWidth={1.5} />
          </button>
        )}
      </div>
        <label className="block text-sm font-medium">Tipo Contenido</label>
        <div className="relative"> 
            <select {...register("enctype")} 
                className= {`appearance-none w-full border n-fmr-input px-4 pr-4 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700`}
            >
                {encTypes.map((e) => (<option key={e} value={e}>{e}</option>))}
            </select>
            <ChevronDown className="w-5 h-5 absolute inset-y-0 top-3 right-3 pointer-events-none text-gray-400" /> 
        </div>
        <label className="block text-sm font-medium">¿Redireccionar?</label>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            {...register("redirect")}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-700"
          />
          <span className="text-sm text-gray-700">Sí</span>
        </div>
        <div className="flex justify-between items-center w-full py-2">
          <span className="mt-1">Encabezados (Headers)</span>
          <button
            type="button"
            onClick={() => appendHeader({ key: "", value: "" })}
            disabled={headerFields.length >= 5}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors border shadow-md
              ${headerFields.length >= 5 ? "bg-gray-200 cursor-not-allowed text-gray-400" : "bg-gray-100 hover:bg-gray-200 btn-inca-secondary border-gray-300"}`}
          >
            <Plus className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        {headerFields.map((header, index) => (
          <div
            key={header.id}
            className="flex flex-col md:flex-row gap-2 items-start md:items-center mb-2 border rounded-2xl p-3 bg-white shadow-sm"
          >
            <div className="flex-1 w-full">
              <input
                placeholder="Clave"
                {...register(`headers.${index}.key`)}
                className={`w-full border n-fmr-input px-3 py-2 ${
                  errors.headers?.[index]?.key ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-1 focus:ring-blue-700`}
              />
              {errors.headers?.[index]?.key && (
                <p className="text-red-500 text-sm mt-1">{errors.headers[index].key?.message}</p>
              )}
            </div>
            <div className="flex-1 w-full">
              <input
                placeholder="Valor"
                {...register(`headers.${index}.value`)}
                className={`w-full border n-fmr-input px-3 py-2 ${
                  errors.headers?.[index]?.value ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-1 focus:ring-blue-700`}
              />
              {errors.headers?.[index]?.value && (
                <p className="text-red-500 text-sm mt-1">{errors.headers[index].value?.message}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeHeader(index)}
              className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300 flex items-center justify-center mt-1 md:mt-0"
            >
              <Trash2 className="w-4 h-4 text-gray-700" strokeWidth={1.5} />
            </button>
          </div>
        ))}
        
        <div className="flex justify-between items-center w-full py-2">
            <span className="mt-1 ">Elemento(s) del formulario</span>        
            <button 
                type="button"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 transition-colors border border-gray-300 hover:bg-gray-200 btn-inca-secondary shadow-md"
                aria-label="Add" 
                onClick={addFieldEditor} 
            >
                <Plus className="w-5 h-5" strokeWidth={1.5} />
            </button>
        </div> 
        {watchedFields.fields?.map((field, index) => (
         
          <div key={index} className="flex justify-between items-center w-full text-sm border rounded-2xl p-2 pl-2  bg-white">
            <span className="flex-1"><span className="font-semibold"> {field.label}</span> {field.type}</span> 
            <div className="flex space-x-2">
              <button 
                  type="button" 
                  aria-label="Add" 
                  className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
                  onClick={() => editFieldEditor(field.field_id)} 
              >
                  <Pencil className="w-4 h-4 m-2" strokeWidth={1.5} />
              </button>
              <button type="button" onClick={() => removeFieldEditor(field.field_id)} 
                  className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
              >
                  <Trash2  className="w-4 h-4 m-2" strokeWidth={1.5} />
              </button>
            </div>
          </div>

        ))}
        
        <label className="block text-sm font-medium">Texto en botón</label>
        <input {...register("labelButton")} placeholder="Texto en botón" 
        className={`w-full border n-fmr-input px-3 py-2 ${errors.title? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-1 focus:ring-blue-700`} />
        {errors.labelButton && <p className="text-red-500">{errors.labelButton.message}</p>}

      </form>

      {/*schemaJson && (
        <pre className="mt-6 bg-gray-100 p-4 rounded overflow-x-hidden overflow-y-hidden scroll-custom ">{JSON.stringify(schemaJson, null, 2)}</pre>
      )*/}
      
      <FieldEditor  
        key={isOpenFieldEditor ? JSON.stringify(fieldEdit) : "default"} 
        isOpen={isOpenFieldEditor} 
        onClose={() => setIsOpenFieldEditor(false)} 
        field={fieldEdit} 
        onSave={handleSaveField} 
        fields={watchedFields.fields as FieldFrm[]}
      />
      <BackendEditor
        isOpen={isOpenBackendEditor}
        onClose={() => setIsOpenBackendEditor(false)}
        backendData={watchedFields.backend_config}
        onSave={handleSaveBackendConfig}
      />
    </div>
  );
}
